import React from 'react';

import { Alert, AlertTitle, Typography, useTheme } from '@mui/material';
import type { SnackbarKey, SnackbarMessage } from 'notistack';
import { useSnackbar } from 'notistack';

import { SnackE2ELocator, SnackVariantKey } from '@clearverify/constants';

import { getIcons } from './icons';
import { SnackMessageWrapper } from './SnackMessageWrapper';
import { buildStyles } from './styles';

type SnackMessageProps = {
  id: SnackbarKey;
  variant: SnackVariantKey;
  message: SnackbarMessage;
  title: React.ReactNode;
};

export const SnackMessage = React.forwardRef<HTMLDivElement, SnackMessageProps>(
  ({ id, variant, message, title }, ref) => {
    const theme = useTheme();
    const { closeSnackbar } = useSnackbar();

    const icons = React.useMemo(() => getIcons(theme), [theme]);
    const { snackVariantsStyles, snackTitleStyles } = React.useMemo(
      () => buildStyles(theme),
      [theme],
    );

    const handleDismiss = React.useCallback(() => {
      closeSnackbar(id);
    }, [id, closeSnackbar]);

    return (
      <SnackMessageWrapper ref={ref}>
        <Alert
          severity={variant}
          sx={snackVariantsStyles[variant]}
          onClose={handleDismiss}
          icon={icons[variant]}
          data-e2e-element={SnackE2ELocator.container}
        >
          <AlertTitle sx={snackTitleStyles} data-e2e-element={SnackE2ELocator.title}>
            {title}
          </AlertTitle>

          <Typography variant="body1" data-e2e-element={SnackE2ELocator.message}>
            {message}
          </Typography>
        </Alert>
      </SnackMessageWrapper>
    );
  },
);
